import {useMemo, useRef, useState} from 'react';
import Autocomplete, {autocompleteClasses} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {useTranslation} from 'react-i18next';

import SelectGroup, {type HeadingCheckbox} from './parts/SelectGroup';
import SelectListPaper from './parts/SelectListPaper';
import SelectOptionRow from './parts/SelectOptionRow';
import ValueChip from './parts/ValueChip';
import {
    type ResolvedOption,
    type SelectOption,
    filterBySearch,
    gatherGroups,
    headingKey,
    isGrouped,
    isSettled,
    optionKey,
    unknownOption
} from './parts/selectOption';

interface MultiSelectProps {
    label: string;
    value: string[];
    options: SelectOption[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    /** Stored names for values absent from options. */
    unknownLabels?: Record<string, string>;
    /** False marks even an empty list as loaded; omit to infer from populated options. */
    loading?: boolean;
    disabled?: boolean;
    /** Disables clearing the whole field; chips can still be removed individually. */
    disableClearable?: boolean;
    /** Adds an edit button to each chip. */
    onEdit?: (value: string) => void;
    /** Heading checkboxes toggle visible, enabled options, including during search. */
    bulkSelect?: boolean;
    /**
     * Values another field on the screen already brings in (a user type, an app). Their rows show
     * ticked without a chip; unticking one opts it out through `onExcludedChange` instead of
     * `onChange`, so the group can lose one member and still pick up new ones.
     */
    impliedValues?: string[];
    /** Implied values opted out; each shows as a red chip whose × brings it back. */
    excludedValues?: string[];
    onExcludedChange?: (values: string[]) => void;
}

const MultiSelect = ({
    label,
    value,
    options,
    onChange,
    placeholder,
    unknownLabels,
    loading,
    disabled,
    disableClearable = false,
    onEdit,
    bulkSelect,
    impliedValues,
    excludedValues,
    onExcludedChange
}: MultiSelectProps) => {
    const {t} = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusedTagRef = useRef<HTMLSpanElement | null>(null);

    // A stable selected array prevents MUI from resetting the search on each keystroke.
    const {allOptions, selected} = useMemo(() => {
        const settled = isSettled(options, loading);
        const availableValues = new Set(options.map(option => option.value));
        const missing = value
            .filter(id => !availableValues.has(id))
            .map(id => unknownOption(id, unknownLabels?.[id], settled, t));

        const allOptions = gatherGroups<ResolvedOption>([...options, ...missing]);
        const selected = value
            .map(id => allOptions.find(option => option.value === id))
            .filter((option): option is ResolvedOption => !!option);

        return {allOptions, selected};
    }, [options, value, unknownLabels, loading, t]);

    // MUI filters before rendering groups; bulk selection must use only those visible rows.
    let visibleOptions: ResolvedOption[] = [];
    const selectedValues = new Set(value);
    const implied = new Set(impliedValues);
    // An exclusion only means something while its value is implied; others are ignored.
    const excluded = (excludedValues ?? []).filter(entry => implied.has(entry));
    const isIn = (entry: string) => selectedValues.has(entry) || (implied.has(entry) && !excluded.includes(entry));

    // MUI only knows `value`, so a press on an implied row arrives as an addition (its row looked
    // ticked) or, when the user had also picked it, as a removal; both toggle the opt-out instead.
    const handleChange = (nextValues: string[]) => {
        let nextExcluded = excluded;
        for (const entry of nextValues.filter(candidate => !selectedValues.has(candidate) && implied.has(candidate))) {
            nextExcluded = nextExcluded.includes(entry)
                ? nextExcluded.filter(candidate => candidate !== entry)
                : [...nextExcluded, entry];
        }
        for (const entry of value.filter(candidate => !nextValues.includes(candidate) && implied.has(candidate))) {
            if (!nextExcluded.includes(entry)) nextExcluded = [...nextExcluded, entry];
        }

        const picks = nextValues.filter(entry => selectedValues.has(entry) || !implied.has(entry));
        if (picks.length !== value.length || picks.some(entry => !selectedValues.has(entry))) onChange(picks);
        if (nextExcluded !== excluded) onExcludedChange?.(nextExcluded);
    };

    const excludedChips = excluded.flatMap((entry, index) => {
        const option = allOptions.find(candidate => candidate.value === entry);
        if (!option) return [];

        return [
            <ValueChip
                key={`excluded-${entry}`}
                option={{...option, tone: 'error'}}
                tagProps={{
                    key: index,
                    className: `${autocompleteClasses.tag} ${autocompleteClasses.tagSizeSmall}`,
                    disabled: !!disabled,
                    'data-tag-index': -1,
                    tabIndex: -1,
                    onDelete: () => onExcludedChange?.(excluded.filter(candidate => candidate !== entry))
                }}
            />
        ];
    });

    const getHeadingCheckbox = (members: SelectOption[]): HeadingCheckbox => {
        const enabledValues = new Set(members
            .filter(option => !option.disabled && !implied.has(option.value))
            .map(option => option.value));
        const picked = Array.from(enabledValues).filter(entry => selectedValues.has(entry));
        const allSelected = enabledValues.size > 0 && picked.length === enabledValues.size;

        return {
            checked: allSelected,
            indeterminate: picked.length > 0 && !allSelected,
            disabled: enabledValues.size === 0,
            onToggle: () => onChange(allSelected
                ? value.filter(entry => !enabledValues.has(entry))
                : [...value, ...Array.from(enabledValues).filter(entry => !selectedValues.has(entry))])
        };
    };

    return (
        <Autocomplete<ResolvedOption, true, boolean, false>
            multiple
            disableCloseOnSelect
            disableClearable={disableClearable}
            clearText={t('Clear')}
            size={'small'}
            fullWidth
            disabled={disabled}
            loading={loading}
            loadingText={t('Loading...')}
            noOptionsText={t('No options')}
            options={allOptions}
            value={selected}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            inputValue={inputValue}
            onInputChange={(_event, next) => setInputValue(next)}
            onChange={(_event, next, reason) => {
                // Clear-all empties the whole field, opt-outs included.
                if (reason === 'clear') {
                    onChange([]);
                    if (excluded.length) onExcludedChange?.([]);
                    return;
                }
                handleChange(next.map(option => option.value));
            }}
            onKeyDown={event => {
                // Require an explicit remove action instead of MUI's empty-input keyboard deletion.
                if (!inputValue && (event.key === 'Backspace' || event.key === 'Delete')) {
                    event.defaultMuiPrevented = true;
                }
            }}
            getOptionLabel={option => option.label}
            getOptionKey={optionKey}
            getOptionDisabled={option => !!option.disabled}
            isOptionEqualToValue={(option, candidate) => option.value === candidate.value}
            groupBy={isGrouped(allOptions) ? headingKey : undefined}
            filterOptions={(candidates, state) => {
                visibleOptions = filterBySearch(candidates, state.inputValue);
                return visibleOptions;
            }}
            PaperComponent={SelectListPaper}
            slotProps={{clearIndicator: {sx: {visibility: 'visible'}}}}
            renderGroup={params => (
                <SelectGroup
                    key={params.key}
                    params={params}
                    visibleOptions={visibleOptions}
                    getHeadingCheckbox={bulkSelect ? getHeadingCheckbox : undefined}
                />
            )}
            renderOption={(props, option) => (
                <SelectOptionRow
                    key={optionKey(option)}
                    itemProps={{...props, 'aria-selected': isIn(option.value)}}
                    option={option}
                    checked={isIn(option.value)}
                />
            )}
            renderTags={(tags, getTagProps, {inputFocused}) => tags.map((option, index) => {
                const tagProps = getTagProps({index});
                return (
                    <ValueChip
                        key={option.value}
                        option={option}
                        tagProps={{
                            ...tagProps,
                            onDelete: event => {
                                // Focus the chip that will occupy MUI's retained tag index after removal.
                                const focusedIndex = inputFocused ? -1 : Number(focusedTagRef.current?.dataset.tagIndex ?? -1);
                                const successorIndex = focusedIndex + (index <= focusedIndex ? 1 : 0);
                                const successor = focusedIndex >= 0 && focusedIndex < tags.length - 1
                                    ? inputRef.current?.closest('.MuiAutocomplete-root')?.querySelector<HTMLElement>(`[data-tag-index="${successorIndex}"]`)
                                    : null;
                                (successor ?? inputRef.current)?.focus();
                                tagProps.onDelete(event);
                            }
                        }}
                        onEdit={onEdit}
                        onFocus={event => {
                            if (event.target === event.currentTarget) focusedTagRef.current = event.currentTarget;
                        }}
                    />
                );
            })}
            renderInput={params => (
                <TextField
                    {...params}
                    inputRef={inputRef}
                    label={label}
                    placeholder={selected.length || excluded.length ? undefined : placeholder}
                    InputLabelProps={{...params.InputLabelProps, shrink: true}}
                    InputProps={{
                        ...params.InputProps,
                        // MUI renders tags only while `value` has some; opt-outs show either way.
                        startAdornment: excludedChips.length
                            ? <>{params.InputProps.startAdornment}{excludedChips}</>
                            : params.InputProps.startAdornment,
                        // Extend MUI's input/field opening behavior to chips, excluding action buttons.
                        onMouseDown: event => {
                            const target = event.target as HTMLElement;
                            if (disabled || target === event.currentTarget || target.closest('input, button')) return;
                            setOpen(current => !current);
                        }
                    }}
                />
            )}
            sx={{
                '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                    flexWrap: 'wrap',
                    height: 'auto'
                },
                '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                    minWidth: 24,
                    width: 0,
                    flexGrow: 1
                }
            }}
        />
    );
};

export default MultiSelect;
